/**
 * Deck / luma device props for Mapbox GL interleaved overlays.
 *
 * Requests `pixelSizeSource: 'css-dpr'` (luma.gl >9.3.3) so tracked pixel size matches
 * Mapbox-style `devicePixelRatio * ceil(cssSize)` instead of ResizeObserver exact boxes.
 * Until that ships on npm, `onDevicePixelRatioChange` / `onResize` apply the same sizing on
 * the shared Mapbox canvas when DPR or layout changes.
 *
 * @see docs/knowledge-base/warnings-interactive-map-resize-dpr.md
 */

import type { CanvasContext, DeviceProps } from "@luma.gl/core";

/** Forward-compatible; `pixelSizeSource` is on luma master, not yet in 9.3.3. */
export type DeckMapboxCanvasContextProps = NonNullable<
  DeviceProps["createCanvasContext"]
> & {
  pixelSizeSource?: "exact" | "css-dpr";
};

function mapboxStyleBufferSize(cssWidth: number, cssHeight: number, dpr: number) {
  const pixelRatio = dpr || 1;
  return {
    width: Math.max(1, Math.round(pixelRatio * Math.ceil(cssWidth))),
    height: Math.max(1, Math.round(pixelRatio * Math.ceil(cssHeight))),
  };
}

function syncSharedCanvasToMapboxStyleDpr(context: CanvasContext) {
  const canvas = context.htmlCanvas;
  if (!canvas) return;

  const cssWidth = canvas.clientWidth || context.cssWidth;
  const cssHeight = canvas.clientHeight || context.cssHeight;
  if (!cssWidth || !cssHeight) return;

  const dpr = window.devicePixelRatio || 1;
  const { width: bufferW, height: bufferH } = mapboxStyleBufferSize(
    cssWidth,
    cssHeight,
    dpr,
  );

  if (canvas.width === bufferW && canvas.height === bufferH) return;

  canvas.width = bufferW;
  canvas.height = bufferH;
  context.setDrawingBufferSize(bufferW, bufferH);
}

export const hotfixLumaDeviceProps: DeviceProps = {
  createCanvasContext: {
    useDevicePixels: true,
    autoResize: false,
    pixelSizeSource: "css-dpr",
  } as DeckMapboxCanvasContextProps,
  onDevicePixelRatioChange: syncSharedCanvasToMapboxStyleDpr,
  onResize: syncSharedCanvasToMapboxStyleDpr,
};
